import { useQuery } from '@apollo/client/react';
import type { FlagsDetailsQueryVariables, FlagsDetailsQuery } from '../../__generated__/graphql-client-types';
import type { FEATURE_FLAGS } from '../../constants/feature-flags';
import { buildExperimentEvaluation } from '../../helpers/analytics/gtm/event-builders';
import { GET_FLAGS_DETAILS } from '../../queries/features/features.queries';
import type { FlagValue } from '../../types/feature-flags.types';
import { doesWindowExist } from '../../utils/dom/dom.utils';
import { useTrackEvent } from '../analytics/analytics.hooks';
import { useRouterLocation } from '../routing/location.hook';

type FeatureHookOptions = {
	isSiteWide?: boolean;
};
export const useFeatures = <T extends FlagValue[] = boolean[]>(
	flagKeys: FEATURE_FLAGS[],
	{ isSiteWide = false }: FeatureHookOptions = {}
): T | never[] => {
	const { pathname } = useRouterLocation();
	const path = isSiteWide ? '*' : pathname;
	const { data, previousData, loading } = useQuery<FlagsDetailsQuery, FlagsDetailsQueryVariables>(GET_FLAGS_DETAILS, {
		variables: { flagKeys, path },
		fetchPolicy: 'cache-first'
	});

	const trackEvent = useTrackEvent({ makeUnique: true });

	const finalData = loading ? previousData : data;

	if (!finalData) {
		return [];
	}

	// track experiment evaluations to Google Analytics
	// track ALL feature flags to Datadog RUM
	finalData.flagsDetails.forEach((flag, index) => {
		const flagKey = flagKeys[index];
		if (flagKey) {
			// Track experiments to Google Analytics
			if (flag.inExperiment) {
				trackEvent(buildExperimentEvaluation({ flagKey, value: flag.value }));
			}

			// Track flags to Datadog RUM
			if (doesWindowExist() && window.DD_RUM && !window.DD_RUM_INIT_ERROR) {
				window.DD_RUM.onReady(() => {
					window.DD_RUM.addFeatureFlagEvaluation(flagKey, flag.value);
				});
			}
		}
	});

	/* This type assertion is necessary because we cannot determine the return
	type of the query at compile time. */
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return finalData.flagsDetails.map((flag) => {
		return flag.value;
	}) as T;
};

export const useFeature = <T extends FlagValue = boolean>(flagKey: FEATURE_FLAGS, options: FeatureHookOptions = {}): T => {
	const [first] = useFeatures<[T]>([flagKey], options);
	return first satisfies T;
};
